
import React from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface LeadStatusSelectProps {
  leadId: string;
  status: string;
  updatingStatus: string | null;
  onStatusChange: (id: string, status: string) => void;
  variant?: 'default' | 'compact';
}

export function LeadStatusSelect({ 
  leadId, 
  status, 
  updatingStatus, 
  onStatusChange,
  variant = 'default'
}: LeadStatusSelectProps) {
  
  const getBadgeVariant = (status: string) => {
    switch(status) {
      case 'interested':
        return 'bg-amber-100 text-amber-800 hover:bg-amber-100';
      case 'contacted':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'waitlist':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
      case 'viewed':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
      case 'accepted':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'rejected':
        return 'bg-red-100 text-red-800 hover:bg-red-100';
      case 'suggested':
        return 'bg-teal-100 text-teal-800 hover:bg-teal-100';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'interested':
        return 'Interested';
      case 'contacted':
        return 'Contacted';
      case 'waitlist':
        return 'On Waitlist';
      case 'viewed':
        return 'Viewed';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'suggested':
        return 'Suggested';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  const isCompact = variant === 'compact';

  return (
    <Select
      disabled={updatingStatus === leadId}
      value={status}
      onValueChange={(value) => onStatusChange(leadId, value)}
    >
      <SelectTrigger 
        className={cn(
          "border-0 p-0 h-auto focus:ring-0 focus:ring-offset-0 shadow-none",
          isCompact && "min-w-0 w-auto"
        )}
      >
        <SelectValue>
          <Badge className={cn(
            getBadgeVariant(status), 
            isCompact ? "px-2 py-1 text-xs" : "px-3 py-1"
          )}>
            {updatingStatus === leadId ? (
              <span className="flex items-center gap-1">
                <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                <span>Updating...</span>
              </span>
            ) : (
              getStatusLabel(status)
            )}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="interested">Interested</SelectItem>
          <SelectItem value="contacted">Contacted</SelectItem>
          <SelectItem value="waitlist">On Waitlist</SelectItem>
          <SelectItem value="viewed">Viewed</SelectItem>
          <SelectItem value="accepted">Accepted</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="suggested">Suggested</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
